import { html, render, nothing } from 'lit';
import { virtual, useEffect, useState, useMemo } from 'haunted';
import { handleModal } from '@local/utils-handle-modal';
import { Loading } from '../loading/index.js';
import { ButtonMoreContent } from '@local/templates';
import { NewsDetail } from '@local/templates/news-detail';
import { formatDate } from '@local/format-date';
import { locale } from '@local/config';
import { getI18n } from './_i18n/i18n.js';

const NewsListView = virtual(() => {
  const [currentItems, setCurrentItems] = useState([]);
  const [nextPage, setNextPage] = useState(null);
  const [moreContentLoading, setMoreContentLoading] = useState(false);

  const currentLocale = locale();

  const literals = useMemo(
    () => getI18n(currentLocale) ?? getI18n('en'),
    [currentLocale]
  );

  useEffect(() => {
    fetch(`/q/news/list`, {
      headers: { 'X-Locale': currentLocale }
    })
      .then(response => response.json())
      .then(data => {
        setCurrentItems(currentItems.concat(data.news));
        setNextPage(data.nextPage);
      });
  }, []);

  function handleMoreNews() {
    setMoreContentLoading(true);
    fetch(`/q/news/list/page/${nextPage}`, {
      headers: { 'X-Locale': currentLocale }
    })
      .then(response => response.json())
      .then(data => {
        const firstId = data.news[0]?.id;
        const newCurrentItems = currentItems.concat(data.news);
        setCurrentItems(newCurrentItems);
        setNextPage(data.nextPage);
        setMoreContentLoading(false);
        setTimeout(() => {
          if (firstId) {
            document.getElementById(`news-item-${firstId}`).focus();
          }
        }, 100);
      });
  }

  return currentItems.length
    ? html`
        <ul
          class="gallery-items grid grid-cols-1 s:grid-cols-2 l:grid-cols-3 gap-4"
        >
          ${currentItems.map(item => {
            const fnHandleNavigate = handleModal({
              view: NewsDetail,
              title: item.title,
              params: {
                id: item.id,
                slug: item.slug,
                locale: currentLocale,
                messages: {},
                baseRoute: ''
              }
            });

            return html`
              <li class="flex flex-col">
                <div
                  class="gallery-items__item-button p-2 cursor-pointer"
                  role="button"
                  tabindex="0"
                  id="news-item-${item.id}"
                  @click="${fnHandleNavigate}"
                  @keydown="${fnHandleNavigate}"
                >
                  ${
                    item.image
                      ? html`
                          <div
                            class="w-full aspect-video relative overflow-hidden mb-2"
                          >
                            <img
                              src="${item.image}"
                              alt=""
                              aria-hidden="true"
                              class="absolute top-1/2 left-0 w-full -translate-y-1/2"
                            />
                          </div>
                        `
                      : nothing
                  }
                  <h3 class="gallery-items__item-title mb-2 text-base">
                    ${item.title}
                  </h3>
                  <p class="gallery-items__text text-xs mb-1">
                    ${formatDate({ value: item.created })}
                  </p>
                  <p class="gallery-items__text text-sm">${item.summary}</p>
                </div>
              </li>
            `;
          })}
        </ul>
        ${
          moreContentLoading
            ? Loading()
            : nextPage
              ? ButtonMoreContent({
                  text: literals.moreNews,
                  eventFn: handleMoreNews
                })
              : nothing
        }
      `
    : Loading();
});

export function NewsList() {
  if (document.getElementById('gallery-news')) {
    render(NewsListView(), document.getElementById('gallery-news'));
  }
}
